import React, { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import {
  ApplyEditMonazamTransaction,
  PreviewEditMonazamTransaction,
} from "../../../../../Actions/LedgerActions";
import { formatAmount } from "../../../../../Util";
import { getEditableTransactionAmount } from "./ledgerEditEligibility";

const EditLedgerModal = ({ isOpen, onClose, transaction, currency, onSuccess }) => {
  const [step, setStep] = useState("form");
  const [newAmount, setNewAmount] = useState("");
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const oldAmount = transaction ? getEditableTransactionAmount(transaction) : 0;

  useEffect(() => {
    if (isOpen && transaction) {
      setStep("form");
      setNewAmount(String(getEditableTransactionAmount(transaction) || ""));
      setReason("");
      setPreview(null);
      setLoading(false);
      setError(null);
    }
  }, [isOpen, transaction]);

  if (!isOpen || !transaction) return null;

  const handlePreview = () => {
    const parsedAmount = parseFloat(newAmount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setError({ message: "New amount must be greater than 0" });
      return;
    }
    if (!reason.trim()) {
      setError({ message: "Reason is required" });
      return;
    }

    PreviewEditMonazamTransaction(
      transaction.id,
      { newAmount: parsedAmount, reason: reason.trim() },
      setLoading,
      (previewData) => {
        setPreview(previewData);
        setStep("preview");
        setError(null);
      },
      setError
    );
  };

  const handleApply = () => {
    const parsedAmount = parseFloat(newAmount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setError({ message: "New amount must be greater than 0" });
      return;
    }
    if (!reason.trim()) {
      setError({ message: "Reason is required" });
      return;
    }

    ApplyEditMonazamTransaction(
      transaction.id,
      { newAmount: parsedAmount, reason: reason.trim() },
      setLoading,
      (result) => {
        onSuccess(transaction.id, result);
        onClose();
      },
      setError
    );
  };

  const handleBack = () => {
    setStep("form");
    setPreview(null);
    setError(null);
  };

  const delta = preview?.delta ?? 0;
  const willGoNegative = preview?.willGoNegative ?? false;

  return (
    <div
      className="modal show d-block"
      onClick={onClose}
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {step === "form" ? "Edit Ledger Amount" : "Confirm Ledger Edit"}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={loading} />
          </div>

          <div className="modal-body">
            <div className="mb-3 small text-muted">
              <div>
                <strong>Date:</strong> {transaction.date?.split("T")[0]}
              </div>
              <div>
                <strong>Narration:</strong> {transaction.narration}
              </div>
              <div>
                <strong>Currency:</strong> {currency}
              </div>
            </div>

            {step === "form" && (
              <>
                <div className="mb-3">
                  <label className="form-label">Current amount</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formatAmount(oldAmount) || oldAmount}
                    disabled
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">New amount *</label>
                  <input
                    type="number"
                    className="form-control"
                    min="0.01"
                    step="0.01"
                    value={newAmount}
                    onChange={(e) => setNewAmount(e.target.value)}
                    disabled={loading}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Reason for edit *</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    maxLength={500}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    disabled={loading}
                    placeholder="Explain why this amount is being corrected"
                  />
                </div>
              </>
            )}

            {step === "preview" && preview && (
              <div className="border rounded p-3 bg-light">
                <div className="row g-2">
                  <div className="col-md-6">
                    <div className="small text-muted">Old amount</div>
                    <div className="fw-bold">{formatAmount(preview.oldAmount)}</div>
                  </div>
                  <div className="col-md-6">
                    <div className="small text-muted">New amount</div>
                    <div className="fw-bold">{formatAmount(preview.newAmount)}</div>
                  </div>
                  <div className="col-md-6">
                    <div className="small text-muted">Delta</div>
                    <div className="fw-bold">{formatAmount(delta)}</div>
                  </div>
                  <div className="col-md-6">
                    <div className="small text-muted">Affected rows</div>
                    <div className="fw-bold">{preview.affectedRows}</div>
                  </div>
                  <div className="col-12">
                    <div className="small text-muted">Stream</div>
                    <div className="small">{preview.streamKey}</div>
                  </div>
                  <div className="col-12">
                    <div className="small text-muted">Balance after edit</div>
                    <div className="fw-bold">{formatAmount(preview.postEditCurrentBalance)}</div>
                  </div>
                </div>
                {willGoNegative && (
                  <div className="alert alert-danger mt-3 mb-0 py-2">
                    This edit would cause a negative running balance. You cannot apply it.
                  </div>
                )}
              </div>
            )}

            {error?.message && (
              <div className="alert alert-danger mt-3 mb-0 py-2">{error.message}</div>
            )}
          </div>

          <div className="modal-footer">
            {step === "form" ? (
              <>
                <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handlePreview} disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Previewing...
                    </>
                  ) : (
                    "Preview"
                  )}
                </button>
              </>
            ) : (
              <>
                <button type="button" className="btn btn-secondary" onClick={handleBack} disabled={loading}>
                  Back
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleApply}
                  disabled={loading || willGoNegative}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    "Confirm"
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditLedgerModal;
